<div class="container privacy">
  <h1>Politique de confidentialité et de protection de la vie privée</h1>

  @if (info$ | async; as info) {
    <p class="intro">
      Cette page explique quelles données personnelles PromoDeep traite, pourquoi, combien de temps, avec qui, et comment
      exercer vos droits, conformément au Règlement général sur la protection des données (RGPD) et à la loi Informatique
      et Libertés.
      @if (info.updatedAt) {
        <span class="updated">Dernière mise à jour : {{ info.updatedAt | date: 'dd/MM/yyyy' }}.</span>
      }
    </p>

    <h2>1. Responsable du traitement</h2>
    <p>
      <b>{{ info.controller.name }}</b>
      @if (info.controller.address) {
        <br>{{ info.controller.address }}
      }
    </p>
    <p>
      Pour toute question sur vos données :
      @if (info.controller.email) {
        <a [href]="'mailto:' + info.controller.email">{{ info.controller.email }}</a>
      } @else {
        <a routerLink="/contact">formulaire de contact</a>
      }@if (info.controller.dpoEmail) {
        — délégué à la protection des données : <a [href]="'mailto:' + info.controller.dpoEmail">{{ info.controller.dpoEmail }}</a>
      }.
    </p>

    <h2>2. Données traitées, finalités et bases légales</h2>

    <h3>Compte membre et communauté</h3>
    <p>
      <b>Données :</b> adresse e-mail, pseudo, mot de passe (enregistré uniquement sous forme chiffrée irréversible),
      nom et prénom si vous les indiquez, photo de profil, description, préférences, deals partagés, commentaires, votes,
      deals sauvegardés, membres suivis ou ignorés, points du Club et demandes de cadeaux, date de dernière activité.
    </p>
    <p>
      <b>Finalités :</b> créer et sécuriser votre compte, publier vos deals et commentaires, gérer le Club des points et
      l'envoi des cadeaux. <b>Base légale :</b> l'exécution des conditions d'utilisation que vous acceptez en vous
      inscrivant (RGPD art. 6.1.b). Votre pseudo, vos deals publiés et vos commentaires sont visibles par tous.
    </p>

    <h3>Messagerie et appels entre membres</h3>
    <p>
      Les messages privés échangés avec vos amis sont enregistrés pour être affichés dans la conversation. Les appels
      audio et vidéo passent directement entre les navigateurs : ils ne sont ni enregistrés ni stockés par PromoDeep.
      <b>Base légale :</b> l'exécution des conditions d'utilisation.
    </p>

    <h3>Newsletter et offres des partenaires</h3>
    <p>
      <b>Données :</b> adresse e-mail, langue, listes choisies (newsletter, offres des partenaires), fréquence et
      historique de vos consentements (date, origine, version du texte accepté, désinscriptions).
    </p>
    <p>
      <b>Finalité :</b> vous envoyer la newsletter PromoDeep et, si vous l'avez accepté séparément, des offres de nos
      partenaires, envoyées par PromoDeep : votre adresse n'est jamais transmise, louée ni vendue aux partenaires.
      <b>Base légale :</b> votre consentement (art. 6.1.a), donné en cochant une case décochée par défaut puis confirmé
      par un lien envoyé à votre adresse. Aucun e-mail n'est envoyé avant cette confirmation. Vous pouvez le retirer à
      tout moment par le lien présent dans chaque e-mail ou dans Paramètres → Abonnements.
    </p>

    <h3>Formulaire de contact</h3>
    <p>
      <b>Données :</b> pseudo, adresse e-mail, objet et message, notre réponse. <b>Finalité :</b> répondre à votre
      demande. <b>Base légale :</b> notre intérêt légitime à traiter les demandes reçues (art. 6.1.f).
    </p>

    <h3>Notifications push</h3>
    <p>
      Si vous activez les notifications, votre navigateur nous transmet un identifiant d'abonnement technique, non relié
      à votre compte, utilisé uniquement pour vous envoyer les nouveaux deals. <b>Base légale :</b> votre consentement,
      retiré à tout moment en désactivant les notifications.
    </p>

    <h3>Cookies, mesure d'audience et publicité</h3>
    <p>
      Aucun cookie de mesure d'audience ni de publicité n'est déposé sans votre accord, donné ou refusé dans la bannière
      cookies (choix conservé 6 mois). Les détails figurent dans notre <a routerLink="/cookies">politique cookies</a>.
      <b>Base légale :</b> votre consentement.
    </p>

    <h3>Liens vers les marchands</h3>
    <p>
      PromoDeep est rémunéré par une commission quand vous achetez après avoir cliqué sur un lien affilié. Le réseau
      d'affiliation (Awin, Amazon…) enregistre alors votre clic et, éventuellement, votre achat, en tant que
      responsable de son propre traitement. PromoDeep ne reçoit aucune donnée permettant de vous identifier.
    </p>

    <h3>Extension Chrome PromoDeep</h3>
    <p>
      L'extension envoie à PromoDeep uniquement le nom de domaine du site que vous visitez (par exemple « darty.com »,
      jamais l'adresse complète de la page), pour afficher les codes promo de ce marchand. Ce nom de domaine n'est ni
      enregistré ni relié à votre compte.
    </p>

    <h3>Sécurité</h3>
    <p>
      Les tentatives de connexion sont limitées pour empêcher les attaques par essais successifs, les échanges sont
      chiffrés (HTTPS) et l'accès aux données dans l'outil d'administration est limité selon le rôle de chaque membre de
      l'équipe. <b>Base légale :</b> notre intérêt légitime à sécuriser le service.
    </p>

    <h2>3. Durées de conservation</h2>
    <p>Ces durées sont appliquées automatiquement chaque nuit :</p>
    <ul class="retention">
      <li>
        <b>Compte membre :</b> jusqu'à sa suppression par vous, ou après {{ duration(info, 'inactiveAccounts') }}
        sans connexion. Vous êtes prévenu par e-mail {{ info.inactiveWarningDays }} jours avant ; il suffit de vous
        connecter pour garder votre compte.
      </li>
      <li><b>Compte dont l'adresse n'a jamais été validée :</b> {{ duration(info, 'unverifiedAccounts') }}.</li>
      <li><b>Messages privés :</b> {{ duration(info, 'chatMessages') }} après leur envoi.</li>
      <li><b>Messages reçus par le formulaire de contact :</b> {{ duration(info, 'contactMessages') }} après leur réception.</li>
      <li><b>Deals partagés refusés en modération :</b> {{ duration(info, 'rejectedSubmissions') }}.</li>
      <li>
        <b>Newsletter :</b> jusqu'à votre désinscription. Ensuite, nous gardons seulement votre adresse et l'historique de
        vos consentements, pour ne plus rien vous envoyer et pouvoir prouver vos choix, pendant
        {{ duration(info, 'newsletterProofs') }} après le dernier changement.
      </li>
      <li><b>Journal des actions de l'équipe de modération :</b> {{ duration(info, 'staffActivities') }}.</li>
      <li><b>Cookies de mesure d'audience et de publicité :</b> 13 mois au plus ; votre choix dans la bannière : 6 mois.</li>
    </ul>
    <p>
      Quand vous supprimez votre compte, vos données sont effacées immédiatement : profil, messages privés, amitiés,
      deals sauvegardés, points, deals partagés en attente ou refusés, messages envoyés par le formulaire de contact.
      Vos commentaires et deals déjà publiés restent visibles, signés « Membre supprimé ». Les demandes de cadeaux déjà
      traitées sont gardées comme justificatif, sans votre pseudo ni votre adresse e-mail.
    </p>

    <h2>4. Destinataires et prestataires</h2>
    <p>
      Vos données sont traitées par PromoDeep et, pour la partie qui les concerne, par les prestataires ci-dessous. Elles
      ne sont jamais vendues.
    </p>
    <ng-template #cards let-list>
      <div class="processors">
        @for (p of list; track $index) {
          <div class="processor">
            <div class="processor-name">
              @if (p.url) {
                <a [href]="p.url" target="_blank" rel="noopener">{{ p.name }}</a>
              } @else {
                {{ p.name }}
              }
            </div>
            <dl>
              <dt>Finalité</dt><dd>{{ p.purpose }}</dd>
              @if (p.data) { <dt>Données</dt><dd>{{ p.data }}</dd> }
              @if (p.location) { <dt>Localisation</dt><dd>{{ p.location }}</dd> }
              @if (p.safeguards) { <dt>Garanties</dt><dd>{{ p.safeguards }}</dd> }
            </dl>
          </div>
        }
      </div>
    </ng-template>
    @if (processorsByRole(info, 'processor'); as list) {
      @if (list.length) {
        <h3>Sous-traitants (agissant pour le compte de PromoDeep)</h3>
        <ng-container *ngTemplateOutlet="cards; context: { $implicit: list }"></ng-container>
      }
    }
    @if (processorsByRole(info, 'recipient'); as list) {
      @if (list.length) {
        <h3>Destinataires responsables de leur propre traitement</h3>
        <ng-container *ngTemplateOutlet="cards; context: { $implicit: list }"></ng-container>
      }
    }
    <p>
      Quand des données sont transférées hors de l'Union européenne, le transfert est encadré par une décision
      d'adéquation de la Commission européenne (EU-US Data Privacy Framework) ou par des clauses contractuelles types.
    </p>

    <h2>5. Vos droits</h2>
    <p>Vous disposez à tout moment, gratuitement, des droits suivants :</p>
    <ul>
      <li><b>Accès et portabilité :</b> téléchargez toutes vos données dans Paramètres → Profil → « Générer mes données ».</li>
      <li><b>Rectification :</b> modifiez votre profil, votre adresse e-mail et vos préférences dans Paramètres.</li>
      <li><b>Effacement :</b> supprimez votre compte dans Paramètres → Profil → « Supprimer mon compte ».</li>
      <li><b>Retrait du consentement et opposition :</b> newsletter, offres des partenaires, cookies et notifications
        se désactivent à tout moment, sans effet sur ce qui a été fait avant.</li>
      <li><b>Limitation</b> du traitement et <b>directives</b> sur le sort de vos données après votre décès.</li>
    </ul>
    <p>
      Pour toute autre demande, écrivez-nous
      @if (info.controller.email) {
        à <a [href]="'mailto:' + info.controller.email">{{ info.controller.email }}</a>
      } @else {
        via le <a routerLink="/contact">formulaire de contact</a>
      }
      : nous répondons sous un mois. Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une
      réclamation à la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener">www.cnil.fr</a>).
    </p>
  }
</div>
